import React, { useRef, useState } from 'react';
import { auth, storage, db } from '../../firebase';
import { UserCircleIcon, CameraIcon, PencilIcon } from '@heroicons/react/24/solid';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import EditUsernameDialog from './EditUsernameDialog';
import { sendEmailVerification } from 'firebase/auth';
import ConfirmDialog from '../common/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import ChangePasswordDialog from './ChangePasswordDialog';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface UserProfileProps {
    profile: {
        username: string;
        email: string;
        role: 'student' | 'teacher' | 'admin';
        status: 'active' | 'inactive';
        avatar?: string;
    };
}

const UserProfile: React.FC<UserProfileProps> = ({ profile }) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isEditingUsername, setIsEditingUsername] = useState(false);
    const [showVerifyEmailDialog, setShowVerifyEmailDialog] = useState(false);
    const [showVerifyEmailSuccess, setShowVerifyEmailSuccess] = useState(false);
    const [showVerifyEmailError, setShowVerifyEmailError] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !auth.currentUser) return;

        try {
            setIsUploading(true);
            const storageRef = ref(storage, `avatars/${auth.currentUser.uid}/${file.name}`);
            await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(storageRef);

            const userRef = doc(db, 'users', auth.currentUser.uid);
            await updateDoc(userRef, {
                avatar: downloadURL
            });

            window.location.reload();
        } catch (error) {
            console.error('Error uploading avatar:', error);
        } finally {
            setIsUploading(false);
        }
    };


    const handleUsernameUpdate = async (newUsername: string): Promise<void> => {
        if (!auth.currentUser) return;

        try {
            const userRef = doc(db, 'users', auth.currentUser.uid);
            await updateDoc(userRef, {
                username: newUsername
            });
            setIsEditingUsername(false);
            window.location.reload();
        } catch (error) {
            console.error('更新用户名失败:', error);
            throw error;
        }
    };

    const handleVerifyEmailClick = () => {
        setShowVerifyEmailDialog(true);
    };

    const handleVerifyEmail = async () => {
        try {
            if (auth.currentUser) {
                await sendEmailVerification(auth.currentUser);
                setShowVerifyEmailDialog(false);
                setShowVerifyEmailSuccess(true);
            }
        } catch (error) {
            console.error('发送验证邮件失败:', error);
            setShowVerifyEmailDialog(false);
            setShowVerifyEmailError(true);
        }
    };

    return (
        <>
            <div className="backdrop-blur-md overflow-hidden sm:rounded-lg">
                <div className="md:flex">
                    {/* 左侧头像区域 */}
                    <div className="md:w-1/3 p-8 sm:p-10 flex flex-col items-center border-b md:border-b-0 md:border-r border-white/10">
                        <motion.div
                            className="relative"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className={`relative rounded-full h-36 w-36 sm:h-44 sm:w-44 ring-4 ring-white/30 shadow-xl overflow-hidden backdrop-blur-sm ${isUploading ? 'opacity-50' : ''
                                }`}>
                                {profile.avatar ? (
                                    <img
                                        src={profile.avatar}
                                        alt={profile.username}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <UserCircleIcon className="h-full w-full text-white/70" />
                                )}
                            </div>
                            <button
                                onClick={handleAvatarClick}
                                disabled={isUploading}
                                className="absolute bottom-2 right-2 p-2.5 rounded-full bg-white text-blue-600 shadow-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-300 transform hover:scale-110"
                            >
                                <CameraIcon className="h-5 w-5" />
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </motion.div>

                        <motion.div
                            className="mt-6 text-center w-full"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <div className="flex items-center justify-center space-x-2">
                                <h2 className="text-2xl font-bold text-white">
                                    {profile.username}
                                </h2>
                                <button
                                    onClick={() => setIsEditingUsername(true)}
                                    className="p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                            </div>
                            <p className="text-sm text-blue-100/80 mt-1">{profile.email}</p>
                            <button
                                onClick={() => setShowChangePassword(true)}
                                className="mt-4 px-4 py-2 text-sm text-white border border-white/20 rounded-full hover:bg-white/10 transition-colors"
                            >
                                {t('profile.changePassword.title')}
                            </button>
                        </motion.div>
                    </div>

                    {/* 右侧信息区域 */}
                    <div className="md:w-2/3 p-8 sm:p-10">
                        <motion.h3
                            className="text-xl font-medium text-white mb-8 pl-2 border-l-4 border-blue-300"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {t('profile.information')}
                        </motion.h3>

                        <div className="space-y-8">
                            {/* 邮箱验证状态 */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                                className="bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                            >
                                <div className="flex justify-between items-baseline">
                                    <label className="block text-sm font-medium text-blue-100">
                                        {t('profile.email.label')}
                                    </label>
                                    {auth.currentUser?.emailVerified ? (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-100 border border-green-500/30">
                                            {t('profile.email.verified')}
                                        </span>
                                    ) : (
                                        <button
                                            onClick={handleVerifyEmailClick}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-100 border border-yellow-500/30 hover:bg-yellow-500/30"
                                        >
                                            {t('profile.email.unverified')}
                                        </button>
                                    )}
                                </div>
                                <p className="mt-2 text-md text-white">{profile.email}</p>
                            </motion.div>

                            {/* 角色信息 */}
                            {profile.role != 'student' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                    className="bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                                >
                                    <label className="block text-sm font-medium text-blue-100">
                                        {t('profile.role.label')}
                                    </label>
                                    <div className="mt-2 flex items-center">
                                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-500/20 text-blue-100 border border-blue-500/30">
                                            {t(`profile.role.${profile.role}`)}
                                        </span>
                                    </div>
                                </motion.div>
                            )}


                            {/* 账号状态 */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                                className="bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-colors"
                            >
                                <label className="block text-sm font-medium text-blue-100">
                                    {t('profile.status.label')}
                                </label>
                                <div className="mt-2">
                                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${profile.status === 'active'
                                        ? 'bg-green-500/20 text-green-100 border border-green-500/30'
                                        : 'bg-red-500/20 text-red-100 border border-red-500/30'
                                        }`}>
                                        {t(`profile.status.${profile.status}`)}
                                    </span>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 用户名编辑对话框 */}
            <EditUsernameDialog
                isOpen={isEditingUsername}
                currentUsername={profile.username}
                onClose={() => setIsEditingUsername(false)}
                onConfirm={handleUsernameUpdate}
            />

            {/* 验证邮件确认对话框 */}
            <ConfirmDialog
                open={showVerifyEmailDialog}
                title={t('profile.verifyEmail.dialogTitle')}
                message={t('profile.verifyEmail.dialogMessage')}
                confirmText={t('profile.verifyEmail.send')}
                cancelText={t('profile.verifyEmail.cancel')}
                onConfirm={handleVerifyEmail}
                onCancel={() => setShowVerifyEmailDialog(false)}
            />

            {/* 发送成功提示 */}
            <ConfirmDialog
                open={showVerifyEmailSuccess}
                title={t('profile.verifyEmail.successTitle')}
                message={t('profile.verifyEmail.successMessage')}
                confirmText={t('profile.verifyEmail.confirm')}
                cancelText={t('profile.verifyEmail.cancel')}
                onConfirm={() => setShowVerifyEmailSuccess(false)}
                onCancel={() => setShowVerifyEmailSuccess(false)}
            />

            {/* 发送失败提示 */}
            <ConfirmDialog
                open={showVerifyEmailError}
                title={t('profile.verifyEmail.errorTitle')}
                message={t('profile.verifyEmail.errorMessage')}
                confirmText={t('profile.verifyEmail.ok')}
                cancelText={t('profile.verifyEmail.cancel')}
                onConfirm={() => setShowVerifyEmailError(false)}
                onCancel={() => setShowVerifyEmailError(false)}
            />

            {/* 修改密码对话框 */}
            <ChangePasswordDialog
                open={showChangePassword}
                onClose={() => setShowChangePassword(false)}
            />
        </>
    );
};

export default UserProfile; 